import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { PageIntro } from "../components/PageIntro.js";
import { Icon } from "../components/Icon.js";
import { useI18n } from "../i18n/I18nProvider.js";
import { useLearnContentRecords } from "../learn/offlineBundle.js";
import type { IndexedLibraryRecord, LibraryIndexResolution } from "./searchIndex.js";
import {
  libraryCategories,
  libraryTopics,
  resolveOfflineLibraryIndex,
  searchLibrary,
} from "./searchIndex.js";

export function LibraryPage() {
  const { locale, t } = useI18n();
  const learnRecords = useLearnContentRecords();
  const [resolution, setResolution] = useState<LibraryIndexResolution>();
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState<string>();
  const [topicId, setTopicId] = useState<string>();
  const [selected, setSelected] = useState<IndexedLibraryRecord>();
  const detailHeading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let active = true;
    const rebuild = () => {
      setResolution(undefined);
      void resolveOfflineLibraryIndex(locale).then((value) => {
        if (active) setResolution(value);
      });
    };
    rebuild();
    window.addEventListener("safesphere:content-state-changed", rebuild);
    return () => {
      active = false;
      window.removeEventListener("safesphere:content-state-changed", rebuild);
    };
  }, [locale]);

  useEffect(() => {
    if (selected !== undefined) detailHeading.current?.focus();
  }, [selected]);

  const index = resolution?.ok ? resolution.index : undefined;
  const categories = index === undefined ? [] : libraryCategories(index);
  const topics = index === undefined ? [] : libraryTopics(index, categoryId);
  const results = useMemo(() => {
    if (index === undefined) return [];
    const searched =
      query.trim().length === 0
        ? index.records.map((record) => ({ record, score: 0, matchedBy: [] as const }))
        : searchLibrary(index, query);
    return searched.filter(
      ({ record }) =>
        (categoryId === undefined || record.category.id === categoryId) &&
        (topicId === undefined || record.topic.id === topicId),
    );
  }, [categoryId, index, query, topicId]);
  const learnResults = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return learnRecords.filter((record) => {
      const metadata = record.metadata;
      const searchable =
        `${t(metadata.title)} ${t(metadata.detail)} ${t(metadata.source)} ${record.practical[locale].serviceDisruption?.intro ?? ""} ${record.practical[locale].serviceDisruption?.actions.map((action) => `${action.title} ${action.text}`).join(" ") ?? ""}`.toLocaleLowerCase();
      return normalized.length === 0 || searchable.includes(normalized);
    });
  }, [learnRecords, query, t]);
  if (selected !== undefined) {
    return (
      <article className="library-detail">
        <button className="button" onClick={() => setSelected(undefined)}>
          {t("library.back")}
        </button>
        <p className="eyebrow">{selected.category.label}</p>
        <h1 ref={detailHeading} tabIndex={-1}>
          {selected.title}
        </h1>
        <p>{selected.summary}</p>
        <dl className="library-metadata">
          <div>
            <dt>{t("library.status")}</dt>
            <dd>{t(selected.status === "VERIFIED" ? "library.verified" : "library.stale")}</dd>
          </div>
          <div>
            <dt>{t("library.topic")}</dt>
            <dd>{selected.topic.label}</dd>
          </div>
          <div>
            <dt>{t("library.jurisdiction")}</dt>
            <dd>{selected.jurisdictionId}</dd>
          </div>
          <div>
            <dt>{t("library.provenance")}</dt>
            <dd>{selected.provenanceReference}</dd>
          </div>
          <div>
            <dt>{t("library.reviewed")}</dt>
            <dd>{selected.reviewedAt.slice(0, 10)}</dd>
          </div>
        </dl>
        <p className="library-fixture-note">{t("library.fixture_note")}</p>
      </article>
    );
  }

  return (
    <div className="page-stack library-page">
      <PageIntro
        body={t("page.search.body")}
        eyebrow={t("page.search.eyebrow")}
        title={t("page.search.title")}
      />
      {resolution === undefined ? (
        <div className="route-state route-state--loading" role="status">
          <span className="route-state__icon">
            <Icon name="search" />
          </span>
          <p>{t("library.loading")}</p>
        </div>
      ) : !resolution.ok ? (
        <>
          <section aria-labelledby="library-search-title" className="library-search-panel">
            <h2 id="library-search-title">
              {locale === "th" ? "ค้นหาในคลังเรียนรู้" : "Search the Learn Library"}
            </h2>
            <label htmlFor="library-query">
              {locale === "th" ? "พิมพ์หัวข้อที่ต้องการ" : "Type a topic"}
            </label>
            <input
              autoComplete="off"
              id="library-query"
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder={
                locale === "th"
                  ? "เช่น น้ำท่วม ปฐมพยาบาล หรือยา"
                  : "For example: flood, first aid, or medicine"
              }
              type="search"
              value={query}
            />
          </section>
          <section
            aria-labelledby="learn-search-results-title"
            className="library-fallback-results"
          >
            <h2 id="learn-search-results-title">
              {locale === "th" ? "บทเรียนที่พบ" : "Learn results"} ({learnResults.length})
            </h2>
            {learnResults.length === 0 ? (
              <div className="library-empty" role="status">
                <span className="route-state__icon">
                  <Icon name="search" />
                </span>
                <div>
                  <strong>{locale === "th" ? "ยังไม่พบบทเรียน" : "No Learn topics found"}</strong>
                  <p>
                    {locale === "th"
                      ? "ลองคำว่า น้ำท่วม แผ่นดินไหว ปฐมพยาบาล หรือยา"
                      : "Try flood, earthquake, first aid, or medicine."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="library-fallback-results__grid">
                {learnResults.map(({ metadata }) => (
                  <Link key={metadata.slug} to={`/learn/${metadata.slug}`}>
                    <Icon name="book" />
                    <span>
                      <strong>{t(metadata.title)}</strong>
                      <small>{t(metadata.detail)}</small>
                    </span>
                    <span aria-hidden="true">↗</span>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </>
      ) : (
        <>
          <section aria-labelledby="library-search-title" className="library-search-panel">
            <h2 id="library-search-title">{t("library.search_title")}</h2>
            <label htmlFor="library-query">{t("library.query_label")}</label>
            <input
              autoComplete="off"
              id="library-query"
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder={t("library.query_placeholder")}
              type="search"
              value={query}
            />
          </section>
          <section aria-labelledby="library-browse-title" className="library-browse">
            <h2 id="library-browse-title">{t("library.browse")}</h2>
            <div className="library-chips" role="group" aria-label={t("library.categories")}>
              <button
                aria-pressed={categoryId === undefined}
                onClick={() => {
                  setCategoryId(undefined);
                  setTopicId(undefined);
                }}
              >
                {t("library.all")}
              </button>
              {categories.map((category) => (
                <button
                  aria-pressed={categoryId === category.id}
                  key={category.id}
                  onClick={() => {
                    setCategoryId(category.id);
                    setTopicId(undefined);
                  }}
                >
                  {category.label}
                </button>
              ))}
            </div>
            <div className="library-chips" role="group" aria-label={t("library.topics")}>
              {topics.map((topic) => (
                <button
                  aria-pressed={topicId === topic.id}
                  key={topic.id}
                  onClick={() => setTopicId(topicId === topic.id ? undefined : topic.id)}
                >
                  {topic.label}
                </button>
              ))}
            </div>
          </section>
          <section aria-labelledby="library-results-title">
            <h2 id="library-results-title">
              {t("library.results")} ({results.length})
            </h2>
            {results.length === 0 ? (
              <div className="library-empty" role="status">
                <span className="route-state__icon">
                  <Icon name="search" />
                </span>
                <div>
                  <strong>{t("library.empty_title")}</strong>
                  <p>{t("library.empty_body")}</p>
                  <p>
                    {[
                      ...categories.map((item) => item.label),
                      ...topics.map((item) => item.label),
                    ].join(" · ")}
                  </p>
                </div>
              </div>
            ) : (
              <ul className="library-results">
                {results.map(({ matchedBy, record }) => (
                  <li key={record.id}>
                    <button onClick={() => setSelected(record)}>
                      <span>{record.category.label}</span>
                      <strong>{record.title}</strong>
                      <small>{record.summary}</small>
                      {matchedBy.length > 0 && (
                        <small>
                          {t("library.matched_by")}:{" "}
                          {matchedBy.map((reason) => t(`library.match.${reason}`)).join(", ")}
                        </small>
                      )}
                      <small>
                        {t(record.status === "VERIFIED" ? "library.verified" : "library.stale")}
                      </small>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <details className="library-diagnostics">
            <summary>{t("library.diagnostics")}</summary>
            <dl>
              <div>
                <dt>{t("library.bundle")}</dt>
                <dd>{index?.bundleId}</dd>
              </div>
              <div>
                <dt>{t("library.locale")}</dt>
                <dd>{index?.locale}</dd>
              </div>
              <div>
                <dt>{t("library.record_count")}</dt>
                <dd>{index?.records.length}</dd>
              </div>
              <div>
                <dt>{t("library.generation")}</dt>
                <dd>{index?.generation}</dd>
              </div>
              <div>
                <dt>{t("library.source")}</dt>
                <dd>{index?.source}</dd>
              </div>
            </dl>
          </details>
        </>
      )}
    </div>
  );
}
